/**
 * PhoneR — international phone number field.
 * Handles type: phone
 */

import { TextField } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { buildValidationRules } from '../validation/fromSchema';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { lookupFieldTooltip } from '../../fieldTooltips';
import { useFormContext } from 'react-hook-form';

interface Props extends RendererProps {
  field: FieldDef;
}

export function PhoneR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  if (pdfMode) {
    const value = getValues(name);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {value ?? <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  return (
    <TextField
      name={name}
      label={label}
      hint={resolveHelpText(field, audience) ?? 'Include country code, e.g. +61 400 000 000'}
      required={field.required}
      rules={buildValidationRules(field)}
      type="tel"
      placeholder="+61 400 000 000"
      tooltip={lookupFieldTooltip(field.canonical_name)}
    />
  );
}
